import { Link as RouterLink, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import MuiLink from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PublishIcon from '@mui/icons-material/Publish'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  useGetAuthoringComponentQuery,
  useGetManifestQuery,
  useGetPublicationChangesQuery,
  useListPublicationsQuery,
} from '../api/authoring'
import type { Manifest, ManifestType } from '../api/authoring'
import { proseOf, typeProse } from '../model/manifest'
import { collectionTitle, listedTypes, members } from '../model/records'
import { routes } from '../model/routes'
import {
  canonicalHost,
  flattenRoutes,
  resolveRouting,
  routeMenus,
  websiteFields,
  type ResolvedRoute,
} from '../model/website'
import { ComponentName } from '../ui/ComponentName'
import { Loading, QueryError } from '../ui/QueryState'
import { useTargetDocument } from '../api/useTargetDocument'
import { VisitsCard } from './OverviewVisits'

/**
 * A site's state of the world.
 *
 * **This is the screen the client did not have.** It opened on a list of
 * sites and then a list of pages, which answers "what exists" — and the
 * question somebody opening an authoring tool is actually asking is
 * "what needs me". Both facts this screen leads with were already on the
 * wire and surfaced nowhere: `…/changes` knows exactly which component
 * revisions differ from the publication a visitor is being served, and
 * the routing tree knows which pages nothing links to.
 *
 * **It shows only what the surface can answer, and says so where it
 * cannot.** How often the site is visited is an answer, since the
 * authoring surface reads the daily rows a nightly collector keeps
 * (docs/plan/site-traffic.md), so the Visits card sits under Live site
 * and says plainly when a site is not connected rather than vanishing.
 * There is still no activity feed, because there is no audit read to
 * build one from — a component's stream carries its own history but
 * nothing enumerates across streams by time, so a feed would either be
 * invented or be a fan-out over every component in the tenant. It is
 * named in `docs/ux/proposed/` as part of the target, would go under the
 * Visits card if it is ever built, and is left out here rather than
 * faked.
 *
 * **Publishing is a link, not a second implementation.** The capture,
 * its polling and its failure states live on the Releases face and are
 * not worth having twice; what this screen owes an author is that the
 * action is *reachable* from where they start, which is the thing the
 * old arrangement got wrong — the sidebar told every reader to publish
 * from Releases while Releases existed only in build mode.
 */
export function Overview() {
  const { tenant = '', site = '' } = useParams()
  const manifest = useGetManifestQuery({ tenant })
  const website = useGetAuthoringComponentQuery({ tenant, id: site })
  const releases = useListPublicationsQuery({ tenant, website: site })
  const changes = useGetPublicationChangesQuery({ tenant, website: site })

  if (website.isLoading) return <Loading label="Reading the site…" />
  if (website.error) return <QueryError error={website.error} title="Could not open this site" />
  if (!website.data) return null

  const fields = websiteFields(website.data)
  const host = canonicalHost(fields)
  const siteRoutes = flattenRoutes(resolveRouting(website.data))
  const current = releases.data?.current
  const published = Boolean(current)
  const pending = changes.data?.changed ? changes.data.changes : []
  const publishedAt = releases.data?.publications.find((entry) => entry.revision === current)?.publishedAt

  // The one check this screen can make without reading every document in
  // the tenant: a route nothing links to. It is the routing tree's own
  // fact, already in hand, and it is the commonest way a page quietly
  // stops existing for a visitor.
  const unlinked = siteRoutes.filter(
    (route) => !route.generated && route.route.page && !route.isHome && routeMenus(route.route).length === 0,
  )

  // The Collection each generated route draws its pages from — the
  // route's own `routeSource` reference, which is `ContentSite`'s
  // reading of the same fact.
  //
  // **It resolves to the Collection, not to the listed type**, and the
  // difference is not academic: a route may source a Collection that no
  // record type declares — t-bluu's selected work is one — and
  // resolving through the type dropped those pages off this card
  // silently. The Collection is what the route names and it is always
  // there, so counting it says something true about every generated
  // route rather than about the ones the manifest happens to describe.
  // The type is then only wanted for the wording, which is where it can
  // be missing without costing a fact.
  const listed = listedTypes(manifest.data)
  const generated = siteRoutes
    .filter((route) => route.generated)
    .map(
      (route) =>
        (website.data.references ?? []).find(
          (reference) => reference.name === 'routeSource' && reference.edgeId === route.route.source,
        )?.id,
    )
    .filter((collection): collection is string => Boolean(collection))
  const unrouted = listed.filter((declared) => !generated.includes(declared.collection ?? ''))

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <Stack spacing={0.5} sx={{ flexGrow: 1, minWidth: 240 }}>
          <Typography variant="h1">{fields.title || fields.name || website.data.id}</Typography>
          <Typography variant="body2" color="text.secondary">
            {published
              ? `Last published ${publishedAt ? new Date(publishedAt).toLocaleString() : '—'} · release ${current}`
              : 'Not published yet. Visitors cannot see this site until it is published.'}
          </Typography>
        </Stack>
        {host && (
          <Button
            component={MuiLink}
            href={`https://${host}`}
            target="_blank"
            rel="noreferrer"
            endIcon={<OpenInNewIcon />}
          >
            View site
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<PublishIcon />}
          component={RouterLink}
          to={routes.siteReleases(tenant, site)}
        >
          {pending.length > 0 ? `Publish ${pending.length} change${pending.length === 1 ? '' : 's'}` : 'Releases'}
        </Button>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          // `minmax(0, …)` at every width: a bare `1fr` cannot be narrower
          // than its widest card, so on a phone one wide card took the
          // whole page sideways with it.
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.7fr) minmax(0, 1fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Stack spacing={3}>
          {/* The release reads are allowed to fail without taking the
              screen with them. They are two of the four reads here and
              the site's own document is the one the screen cannot do
              without — so a publication history that does not answer
              costs this panel and the table below it, and leaves the
              routing facts standing. Saying "could not be read" and
              pointing at Releases is the honest version; a green
              "everything is live" drawn over a failed read is the
              dangerous one. */}
          {releases.error || changes.error ? (
            <Alert
              severity="error"
              action={
                <Button component={RouterLink} to={routes.siteReleases(tenant, site)}>
                  Open Releases
                </Button>
              }
            >
              Could not check what is live, so this page cannot say what visitors are currently seeing.
            </Alert>
          ) : changes.isLoading || releases.isLoading ? (
            <Alert severity="info">Checking what is live…</Alert>
          ) : !published ? (
            <Alert
              severity="warning"
              action={
                <Button component={RouterLink} to={routes.siteReleases(tenant, site)}>
                  Publish it
                </Button>
              }
            >
              This site has not been published yet, so visitors cannot see it.
            </Alert>
          ) : pending.length > 0 ? (
            <Alert
              severity="warning"
              action={
                <Button component={RouterLink} to={routes.siteReleases(tenant, site)}>
                  Review &amp; publish
                </Button>
              }
            >
              <strong>
                {pending.length} change{pending.length === 1 ? ' is' : 's are'} waiting to go live.
              </strong>{' '}
              Edited since the last release. Visitors still see the earlier version until you publish.
            </Alert>
          ) : (
            <Alert severity="success">Everything on this site is live. Nothing is waiting to be published.</Alert>
          )}

          {pending.length > 0 && (
            <Paper>
              <Stack direction="row" alignItems="baseline" sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h3" sx={{ flexGrow: 1 }}>
                  Waiting to go live
                </Typography>
                {/* A site never published has no release to date its changes
                    from, and every one of them is new. */}
                {current ? (
                  <Typography variant="caption" color="text.secondary">
                    since release {current}
                  </Typography>
                ) : null}
              </Stack>
              {/* Fixed, so the name is the column that gives way: it is
                  drawn on one line, and an automatic layout widened the
                  table to the longest one — or to an id, while the name
                  is read — and pushed the page sideways on a phone. On a
                  phone the kind goes too: the name is what says which
                  change it is, and beside two fixed columns it had room
                  for a word. */}
              <Table sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell>What</TableCell>
                    <TableCell sx={{ width: 128, display: { xs: 'none', sm: 'table-cell' } }}>Kind</TableCell>
                    <TableCell align="right" sx={{ width: { xs: 88, sm: 112 } }}>
                      Change
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pending.map((change) => (
                    <ChangeRow
                      key={change.id}
                      tenant={tenant}
                      id={change.id}
                      currentRevision={change.currentRevision}
                      publishedRevision={change.publishedRevision}
                    />
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {unlinked.length > 0 && (
            <Paper>
              <Stack sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h3">Needs attention</Typography>
              </Stack>
              <Table>
                <TableBody>
                  {unlinked.map((route) => (
                    <TableRow key={route.urlPath}>
                      {/* On a phone the icon goes and the action moves
                          under the words: three cells side by side, one
                          of them a button that cannot wrap, were wider
                          than a 320px screen. */}
                      <TableCell sx={{ width: 28, color: 'text.disabled', display: { xs: 'none', sm: 'table-cell' } }}>
                        <VisibilityOffIcon fontSize="small" />
                      </TableCell>
                      <TableCell>
                        {/* A title can be one long word, a slug most of all. */}
                        <Typography variant="body2" sx={{ fontWeight: 550, overflowWrap: 'anywhere' }}>
                          {route.route.title || route.route.slug} is not in any menu
                        </Typography>
                        {/* "No menu carries it" is what this check
                            actually knows, and the copy is held to it.
                            A page can still be linked from the words on
                            another page — a body is rich text in a
                            stream this screen never reads — so telling
                            an owner that nothing links to it would be
                            asserting something no read here supports. */}
                        <Typography variant="caption" color="text.secondary">
                          It is not in any menu, so visitors can only reach it by a link from another page or by typing
                          its address.
                        </Typography>
                        <Box sx={{ display: { sm: 'none' }, mt: 0.5, ml: -1 }}>
                          <PageLink tenant={tenant} site={site} route={route} />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>
                        <PageLink tenant={tenant} site={site} route={route} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Stack>

        <Stack spacing={3}>
          <Paper>
            <Stack sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h3">Live site</Typography>
            </Stack>
            <Stack spacing={1.5} sx={{ px: 2, py: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                {published ? (
                  <Chip size="small" color="success" label={`Live · release ${current}`} />
                ) : (
                  <Chip size="small" color="warning" label="Not published" />
                )}
              </Stack>
              {host ? (
                <MuiLink href={`https://${host}`} target="_blank" rel="noreferrer" variant="body2">
                  {host}
                </MuiLink>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No web address is set up yet.
                </Typography>
              )}
              <Divider />
              {/* The counts sit as tiles across the card rather than as a
                  ladder of rows down it, and the reason is the rule just
                  above: a row can now disappear. A ladder whose length
                  depends on how many lists a tenant keeps is a card that
                  is two rows tall on one site and seven on another, and
                  the right-hand column was already the short one — a
                  column of `label ………… 7` set against that much empty
                  space reads as a card that ran out of things to say.
                  Tiles wrap, so one count and five both fill the width
                  they are given and the card keeps its proportions.

                  The column is no longer short for want of a read: the
                  Visits card under this one is what the comp's activity
                  feed position now holds, because the surface answers it.
                  A feed, which still has no read behind it, would go under
                  that rather than be invented here. */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
                  gap: 2,
                  rowGap: 2.5,
                }}
              >
                {/* Pages a visitor can reach, counted the way a visitor
                    would meet them rather than the way the routing tree
                    stores them.

                    **A generated route is one row of the tree and ten
                    pages on the site**, so counting the row says "1"
                    about a thing there are ten of — a label and a number
                    that disagree, which on a card whose whole job is a
                    glance is worse than the row being absent. The count
                    comes from the Collection the route draws on — the
                    same read, under the same cache key, that the site
                    screen's Lists panel makes one click away.

                    Zero is hidden, because a card read at a glance should
                    carry facts rather than the absence of them: a site
                    with no menu headings has nothing to say about menu
                    headings. Pages is the exception and is marked so — a
                    site with no pages is exactly the finding somebody
                    opening this screen needs, and a card that answered it
                    with silence would be the screen failing at its one
                    job. */}
                <Count
                  label="Pages"
                  value={siteRoutes.filter((route) => !route.generated && route.route.page).length}
                  always
                />
                {generated.map((collection, index) => (
                  <GeneratedCount
                    key={`${collection}-${index}`}
                    tenant={tenant}
                    collection={collection}
                    manifest={manifest.data}
                  />
                ))}
                <Count
                  label="Menu headings"
                  value={siteRoutes.filter((route) => !route.generated && !route.route.page).length}
                />
                {/* The lists the owner keeps that no route turns into
                    pages of their own — the comp's "Photos 80". Listing
                    them here as well as above would put one number on the
                    card twice under two labels, which is the disagreement
                    this card was just fixed for. */}
                {unrouted.map((declared) => (
                  <RecordCount
                    key={declared.name}
                    tenant={tenant}
                    declared={declared}
                    label={proseOf(declared, declared.name).plural}
                  />
                ))}
              </Box>
            </Stack>
          </Paper>
          {/* Its own read, which may fail or be slow without touching
              anything above it, by the rule the release reads follow. */}
          <VisitsCard tenant={tenant} site={site} />
        </Stack>
      </Box>

      <Typography variant="caption" color="text.secondary">
        This page shows the site as it is now, and how often it has been visited. A history of who changed what, and
        when, is not available yet.
      </Typography>
    </Stack>
  )
}

/**
 * One differing component, named.
 *
 * The listing gives an id and two revisions and nothing else — what a
 * component *is* lives in its own stream — so the name and the type are a
 * point read per row, the same trade `ComponentName` already makes and
 * for the same reason: a uuid on screen is the dearest thing here.
 */
function ChangeRow({
  tenant,
  id,
  currentRevision,
  publishedRevision,
}: {
  tenant: string
  id: string
  currentRevision: number
  publishedRevision?: number
}) {
  const manifest = useGetManifestQuery({ tenant })
  const target = useTargetDocument(tenant, id)
  const prose = target.data ? typeProse(manifest.data, target.data.type) : undefined

  return (
    <TableRow>
      <TableCell>
        <MuiLink component={RouterLink} to={routes.component(tenant, id)} sx={{ fontWeight: 550 }}>
          <ComponentName tenant={tenant} id={id} manifest={manifest.data} />
        </MuiLink>
      </TableCell>
      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
        <Chip size="small" variant="outlined" label={prose?.title ?? '…'} />
      </TableCell>
      <TableCell align="right">
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>
          {publishedRevision ? `r${publishedRevision} → r${currentRevision}` : `new at r${currentRevision}`}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

/** A route's page, where it has one to open. */
function PageLink({ tenant, site, route }: { tenant: string; site: string; route: ResolvedRoute }) {
  if (!route.targetId) return null
  return (
    <Button component={RouterLink} to={routes.pageSettings(tenant, site, route.targetId)}>
      Add to a menu
    </Button>
  )
}

/**
 * How many pages one generated route puts on the site.
 *
 * It is the count of the Collection the route sources, because that is
 * what the route means: one page per member, addressed by the pattern
 * the route carries. The number is therefore a fact about the *site* —
 * pages a visitor can open — rather than about what the owner keeps,
 * even though the read behind it is the same read.
 *
 * **The wording is where the listed type is wanted, and where it is
 * allowed to be missing.** A Collection a record type declares gets that
 * type's own singular — "Album pages", the comp's wording — because the
 * owner already calls them albums. A Collection no type declares has no
 * such word, so the tile names the list instead, through
 * `collectionTitle`, which is the one place that decides what to call a
 * Collection and already prefers the author's own label over anything
 * derived.
 */
function GeneratedCount({
  tenant,
  collection,
  manifest,
}: {
  tenant: string
  collection: string
  manifest: Manifest | undefined
}) {
  const document = useGetAuthoringComponentQuery({ tenant, id: collection })
  const declared = listedTypes(manifest).find((listed) => listed.collection === collection)
  const label = declared
    ? `${proseOf(declared, declared.name).title} pages`
    : `Pages from ${collectionTitle(manifest, collection, document.data?.label)}`
  return <Count label={label} value={document.isLoading ? undefined : members(document.data).length} />
}

/**
 * How many records of one listed type this tenant keeps — which is also
 * how many pages a route over that type puts on the site, one each.
 *
 * It is a read of the Collection the type declares, which is the same
 * read the site screen's Lists panel makes under the same cache key —
 * so counting them here costs no request the app was not already going
 * to make when the reader clicks through. `members` is the one reading
 * of what a Collection holds, for the reason every other shared model
 * function exists: two readings of one document is where two screens
 * start disagreeing about a number.
 *
 * **The label is the caller's**, because the same count answers two
 * different questions depending on whether a route publishes it: "Album
 * pages" is a fact about the site, "Albums" is a fact about what the
 * owner keeps. The number is identical and the sentence it belongs to
 * is not.
 */
function RecordCount({ tenant, declared, label }: { tenant: string; declared: ManifestType; label: string }) {
  const collection = useGetAuthoringComponentQuery(
    { tenant, id: declared.collection ?? '' },
    { skip: !declared.collection },
  )
  if (!declared.collection) return null
  return <Count label={label} value={collection.isLoading ? undefined : members(collection.data).length} />
}

/**
 * One label and its number.
 *
 * **A zero is withheld unless the caller says the zero is the point.**
 * A card read at a glance earns its place by carrying facts, and "this
 * site has no menu headings" is not one — it is a row the eye has to
 * read to discover that it did not need to. The exception is a zero
 * that is itself the finding, which is why the decision is the caller's
 * and not a rule applied here.
 *
 * A count still being read shows an ellipsis rather than disappearing:
 * a row that is absent because the answer is zero and a row that is
 * absent because the answer has not arrived are different things, and
 * only one of them should be silent.
 */
function Count({ label, value, always }: { label: string; value: number | undefined; always?: boolean }) {
  if (value === 0 && !always) return null
  return (
    <Stack spacing={0.25}>
      {/* The number first and larger, because it is what the eye came
          for; the label under it in the same grey every other secondary
          line on this screen uses. Tabular figures so a column of them
          lines up on the digit rather than on the glyph. */}
      <Typography sx={{ fontSize: 22, lineHeight: 1.1, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value ?? '…'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  )
}
